import { useEffect, useRef } from 'react';
import { type AriaDropDownProps } from './common';
import DropDownOptionEl from './DropDownOption';
import { useLanguageContext } from '../../core/locale/LanguageContext';

interface DropDownOptionsProps {
  className?: string;
  dropDownAria: AriaDropDownProps;
  options: DropDownOption[];
  focusElementId?: string;
  onExit: () => void;
}

export interface DropDownOption {
  element: React.ReactNode;
  id: string;
  className: string;
  ariaLabel: string;
  onClick: (e: React.UIEvent) => void;
  name: string;
}

function DropDownOptions({
  dropDownAria,
  options,
  className,
  focusElementId,
  onExit,
}: DropDownOptionsProps): React.ReactNode {
  const languageContext = useLanguageContext();

  const itemsRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, options.length);
  }, [options]);

  useEffect(() => {
    if (focusElementId) {
      const i = options.findIndex((o) => o.id === focusElementId);
      if (i >= 0) {
        itemsRef.current[i].focus();
      }
    }
  }, [focusElementId]);

  const onArrowKey = (newIndex: number) => {
    const i = Math.max(0, Math.min(newIndex, options.length - 1));
    itemsRef.current[i].focus();
  };

  if (!options.length || !dropDownAria.expanded) return <></>;

  return (
    <div
      className={`${className} ${dropDownAria.expanded && 'show'}`}
      id={dropDownAria.controlsId}
      role="listbox"
      aria-label={languageContext.getLocalizedText('dropDownOptionsLabel')}
      tabIndex={-1}
    >
      {options
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((option, i) => (
          <DropDownOptionEl
            ariaLabel={option.ariaLabel}
            onClick={option.onClick}
            dropDownAria={dropDownAria}
            key={option.id}
            id={option.id}
            className={option.className}
            index={i}
            ref={(el) => {
              if (el) itemsRef.current[i] = el;
            }}
            onArrowKey={onArrowKey}
            onExit={onExit}
          >
            {option.element}
          </DropDownOptionEl>
        ))}
    </div>
  );
}

export default DropDownOptions;
